import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { authAPIClient } from '../lib/authAPI'
import { taskService } from '../services/taskService'
import { AssessmentStorage } from '../utils/assessmentStorage'
import { onBrowserSessionInvalidated } from '../services/browserState'

const OrganisationContext = createContext(null)

export const OrganisationProvider = ({ children }) => {
  const [memberships, setMemberships] = useState([])
  const [organisationId, setOrganisationId] = useState(null)
  const [engagementId, setEngagementId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const clearContext = useCallback(() => {
    setMemberships([])
    setOrganisationId(null)
    setEngagementId(null)
    taskService.clearOrganisation()
    AssessmentStorage.clearContext()
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await authAPIClient.makeRequest('/organisation-context', { method: 'GET' })
      const activeMemberships = response.memberships || []
      setMemberships(activeMemberships)
      const first = activeMemberships[0]
      const nextOrganisationId = first?.organisation_id || null
      const nextEngagementId = first?.engagements?.find(item => item.status !== 'closed')?.id || first?.engagements?.[0]?.id || null
      setOrganisationId(nextOrganisationId)
      setEngagementId(nextEngagementId)
    } catch (loadError) {
      clearContext()
      setError('Unable to verify organisation membership')
    } finally {
      setLoading(false)
    }
  }, [clearContext])

  useEffect(() => {
    load()
    return onBrowserSessionInvalidated(clearContext)
  }, [load, clearContext])

  useEffect(() => {
    if (organisationId) taskService.setOrganisation(organisationId, engagementId)
    else taskService.clearOrganisation()
    AssessmentStorage.configure({ organisationId, engagementId })
  }, [organisationId, engagementId])

  const selectOrganisation = useCallback(nextOrganisationId => {
    const membership = memberships.find(item => item.organisation_id === nextOrganisationId && item.status === 'active')
    if (!membership) throw new Error('Organisation membership is not active')
    setOrganisationId(nextOrganisationId)
    setEngagementId(membership.engagements?.find(item => item.status !== 'closed')?.id || membership.engagements?.[0]?.id || null)
  }, [memberships])

  const selectEngagement = useCallback(nextEngagementId => {
    const membership = memberships.find(item => item.organisation_id === organisationId)
    if (!membership?.engagements?.some(item => item.id === nextEngagementId && item.organisation_id === organisationId)) {
      throw new Error('Engagement is not available for the active organisation')
    }
    setEngagementId(nextEngagementId)
  }, [memberships, organisationId])

  const value = useMemo(() => ({ memberships, organisationId, engagementId, loading, error, reload: load, selectOrganisation, selectEngagement }), [memberships, organisationId, engagementId, loading, error, load, selectOrganisation, selectEngagement])
  return <OrganisationContext.Provider value={value}>{children}</OrganisationContext.Provider>
}

export const useOrganisation = () => {
  const context = useContext(OrganisationContext)
  if (!context) throw new Error('useOrganisation must be used within OrganisationProvider')
  return context
}

export const OrganisationGate = ({ children }) => {
  const { memberships, organisationId, engagementId, loading, error, reload, selectOrganisation, selectEngagement } = useOrganisation()
  if (loading) return <div className="p-8 text-center">Verifying organisation access...</div>
  if (error) return <div className="p-8 text-center"><p role="alert">{error}</p><button onClick={reload}>Retry</button></div>
  if (!organisationId) return <div className="p-8 text-center" role="alert">No active organisation membership is available.</div>
  const current = memberships.find(item => item.organisation_id === organisationId)
  return (
    <>
      {memberships.length > 1 && (
        <label className="sr-only" htmlFor="organisation-context">Organisation</label>
      )}
      {memberships.length > 1 && <select id="organisation-context" value={organisationId} onChange={event => selectOrganisation(event.target.value)}>{memberships.map(item => <option key={item.organisation_id} value={item.organisation_id}>{item.organisations?.name || 'Organisation'}</option>)}</select>}
      {current?.engagements?.length > 1 && <select aria-label="Engagement" value={engagementId || ''} onChange={event => selectEngagement(event.target.value)}>{current.engagements.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select>}
      {!engagementId ? <div className="p-8 text-center" role="alert">No engagement is available for this organisation.</div> : children}
    </>
  )
}
